import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import UserFormModal from '../components/UserFormModal';
import ResetUserPasswordModal from '../components/ResetUserPasswordModal';
import '../styles/companiespage.css';
import '../styles/userspage.css';

const TYPE_LABEL = { PAID: 'Paid', UNPAID: 'Unpaid' };

// Admin-only page: add users, choose their company access, reset passwords,
// activate/deactivate, and jump to each user's offer history.
export default function UsersPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formUser, setFormUser] = useState(undefined); // undefined = closed, null = add, object = edit
  const [resetUser, setResetUser] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [usersRes, companiesRes] = await Promise.all([api.get('/users'), api.get('/companies')]);
      setUsers(usersRes.data.users);
      setCompanies(companiesRes.data.companies);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load users.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleStatus = async (u) => {
    const action = u.isActive ? 'Deactivate' : 'Reactivate';
    const detail = u.isActive
      ? `${u.name} won't be able to sign in. Their offer history is kept.`
      : `${u.name} will be able to sign in again with their current password.`;
    if (!window.confirm(`${action} ${u.name}?\n\n${detail}`)) return;

    try {
      await api.patch(`/users/${u._id}/status`, { isActive: !u.isActive });
      load();
    } catch (err) {
      alert(err.response?.data?.message || `Could not ${action.toLowerCase()} this user.`);
    }
  };

  const statusBadge = (u) => {
    if (!u.isActive) return <span className="hpu-status hpu-status--inactive">Deactivated</span>;
    if (u.mustChangePassword) return <span className="hpu-status hpu-status--pending">Awaiting first sign-in</span>;
    return <span className="hpu-status hpu-status--active">Active</span>;
  };

  // Only show access for companies that still exist
  const activeAccess = (u) => (u.permissions || []).filter((p) => p.company && p.company.isActive);

  return (
    <div className="hpc-page">
      <div className="hpc-topbar">
        <h1 className="hpc-title">Users</h1>
        <button className="hpc-btn hpc-btn--primary" onClick={() => setFormUser(null)}>
          <PlusIcon /> Add user
        </button>
      </div>

      <div className="hpc-table-card">
        {loading ? (
          <div className="hpc-state">
            <div className="hpc-spinner" aria-hidden="true" />
            <p>Loading users…</p>
          </div>
        ) : error ? (
          <div className="hpc-state">
            <p>{error}</p>
            <button className="hpc-btn hpc-btn--outline hpc-btn--sm" onClick={load}>Try again</button>
          </div>
        ) : users.length === 0 ? (
          <div className="hpc-state">
            <UsersIcon />
            <p>No users yet. Add someone and choose which companies and offer types they can use.</p>
          </div>
        ) : (
          <div className="hpu-table-scroll">
            <table className="hpc-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Access</th>
                  <th>Status</th>
                  <th>Last sign-in</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const access = activeAccess(u);
                  return (
                    <tr key={u._id} className={u.isActive ? '' : 'hpu-row-inactive'}>
                      <td>
                        <div className="hpc-company-name">{u.name}</div>
                        <div className="hpu-user-email">{u.email}</div>
                      </td>
                      <td>
                        {access.length === 0 ? (
                          <span className="hpc-unset">no access</span>
                        ) : (
                          <div className="hpu-access">
                            {access.map((p) => (
                              <span key={p.company._id} className="hpu-chip">
                                <span className="hpu-swatch" style={{ background: p.company.accentColor }} />
                                {p.company.shortName}
                                <span className="hpu-chip-types">
                                  {p.offerTypes.map((t) => TYPE_LABEL[t]).join(', ')}
                                </span>
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td>{statusBadge(u)}</td>
                      <td className="hpu-signin">
                        {u.lastLoginAt ? (
                          <>
                            {new Date(u.lastLoginAt).toLocaleDateString()}
                            <span className="hpu-signin-time">
                              {new Date(u.lastLoginAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </>
                        ) : (
                          <span className="hpc-muted">Never</span>
                        )}
                      </td>
                      <td>
                        <div className="hpu-actions">
                          <button
                            className="hpc-btn hpc-btn--outline hpc-btn--sm"
                            onClick={() => navigate(`/history?userId=${u._id}`)}
                          >
                            View history
                          </button>
                          <button className="hpc-btn hpc-btn--outline hpc-btn--sm" onClick={() => setFormUser(u)}>
                            Edit
                          </button>
                          <button className="hpc-btn hpc-btn--outline hpc-btn--sm" onClick={() => setResetUser(u)}>
                            Reset password
                          </button>
                          <button
                            className={`hpc-btn hpc-btn--sm ${u.isActive ? 'hpc-btn--danger' : 'hpc-btn--outline'}`}
                            onClick={() => toggleStatus(u)}
                          >
                            {u.isActive ? 'Deactivate' : 'Reactivate'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {formUser !== undefined && (
        <UserFormModal
          existing={formUser}
          companies={companies}
          onClose={() => setFormUser(undefined)}
          onSaved={() => { setFormUser(undefined); load(); }}
        />
      )}

      {resetUser && (
        <ResetUserPasswordModal
          user={resetUser}
          onClose={() => { setResetUser(null); load(); }}
        />
      )}
    </div>
  );
}

function PlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <line x1="12" y1="4" x2="12" y2="20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="4" y1="12" x2="20" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg width="34" height="34" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="9" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M2.5 20c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M16 4.6a3.5 3.5 0 0 1 0 6.8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M18 14.4c2.1.7 3.5 2.8 3.5 5.6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
